// The swipe city-switch's numbers, straight from the live game's SwipeSwitcher.cs (Front End/Assets/Scripts/SwipeSwitch).
// Kept pure so the unit tests pin them. Positions are in the game canvas's units (reference width 800, matched on width):
// multiply by viewportWidth / 800 for CSS px. Unity's y and rotation point up / counter-clockwise; CSS's down / clockwise.

export type SwipeDir = 1 | -1; // 1 = next city (finger moves left), -1 = previous city (finger moves right)

export const REF_W = 800; // CanvasScaler m_ReferenceResolution.x, MatchWidthOrHeight 0
export const ANCHOR = 1300; // anchorLeft / anchorRight
export const PARALLAX = 50; // the main container's shift
export const SCROLL_SPEED = 2; // a drag of half the screen width = the whole switch

/** The direction a horizontal drag asks for. */
export const dirOf = (dx: number): SwipeDir => (dx < 0 ? 1 : -1);

/** How far through the switch a drag is: |dx| / (width / scrollSpeed). 0.5 is the threshold — release past it switches. */
export const swipePct = (dx: number, width: number): number => (width > 0 ? Math.abs(dx) / (width / SCROLL_SPEED) : 0);

/** The neighbouring city in the game's own order, wrapping round at both ends; null when there's nowhere else to go. */
export function neighbour(ids: number[], cur: number, dir: SwipeDir): number | null {
  if (ids.length < 2) return null;
  const i = ids.indexOf(cur);
  if (i < 0) return null;
  return ids[(i + dir + ids.length) % ids.length];
}

/** Mathf.Lerp(pct, target, 5 * dt): the release / cancel glide, frame by frame. */
export const glide = (pct: number, target: number, dt: number): number => pct + (target - pct) * Math.min(1, Math.max(0, 5 * dt));

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export interface SwipeFrame {
  /** the cloud cover's x (units); the top clouds ride at 1.2× for parallax */
  cover: number;
  top: number;
  /** the screen behind: nudged away from the incoming clouds, then settles back from the other side */
  main: number;
  /** the city name / arrow / "Go to next city" block */
  helpX: number;
  helpAlpha: number;
  /** the arrow's tilt, CSS degrees (clockwise) */
  arrowDeg: number;
}

export function swipeFrame(pct: number, dir: SwipeDir): SwipeFrame {
  const start = dir === 1 ? ANCHOR : -ANCHOR;
  const end = -start;
  const cover = lerp(start, end, clamp01(pct));
  const main = pct < 0.5
    ? lerp(0, dir === 1 ? -PARALLAX : PARALLAX, pct * 2)
    : lerp(dir === 1 ? PARALLAX : -PARALLAX, 0, (pct - 0.5) * 2);
  const r = (Math.min(0.5, Math.max(0.25, Math.abs(pct))) - 0.25) / 0.25;
  const unityDeg = 15 * (dir === 1 ? 1 - r : -1 + r);
  return {
    cover,
    top: cover * 1.2,
    main,
    helpX: lerp(start, 0, clamp01(pct * 2)) * 0.2,
    helpAlpha: clamp01(1 - Math.abs(pct - 0.5) * 4),
    arrowDeg: unityDeg === 0 ? 0 : -unityDeg,
  };
}

/** The clouds as the prefab lays them out (PrevClouds: 7, PrevCloudsTop: 3): centre x/y, w/h in units, rotation in CSS
 *  degrees (from the RectTransform's quaternion z). */
export interface CloudSpec { x: number; y: number; w: number; h: number; deg: number }
const q2deg = (z: number) => -(2 * Math.asin(z) * 180) / Math.PI;
const c = (x: number, y: number, w: number, h: number, z: number): CloudSpec => ({ x, y, w, h, deg: q2deg(z) });
export const COVER_CLOUDS: CloudSpec[] = [
  c(275, 521, 1000, 1000, 0.7384431),
  c(331, -58, 1000, 1000, 0.96165925),
  c(-7, 0, 1374.337, 1605.284, 0.99996257),
  c(-315, -564, 1000, 1000, 0.79309684),
  c(-246, 553, 1000, 1000, 0),
  c(244, -496, 1000, 1000, 0.22181343),
  c(-332, -123, 1000, 1000, 0.5449176),
];
export const TOP_CLOUDS: CloudSpec[] = [
  c(98, 16, 1168.061, 1168.061, 0.73305666),
  c(-227, -726, 1000, 1000, 0.06727673),
  c(-91, 809, 1284.012, 1284.012, 0.3271686),
];
